/**
 * PATH: packages/bills/src/paymentTerms.ts
 * FILE_ID: BILLS-PKG-PAYMENT-TERMS-001
 * PURPOSE: Front-end mirror of the backend supplier payment-term taxonomy.
 *
 * WHY: The supplier's internal payment term (CoD / EOD / 7 / 30 / 45 / 60 /
 *      90 / EOM / Custom) drives a bill's predicted due date. The select in
 *      BillPaymentSection needs the option list + a human label, and the
 *      `code → label` map is the safety net for rendering a term the backend
 *      sent before the picker re-fetches `supplier_detail.payment_term_label`.
 *      Kept out of `types.ts` so that file stays a pure type module.
 *
 * FLOW: backend quotebuilder/payment_terms.py (SupplierTermType + term_label)
 *        ─→ this module (SUPPLIER_TERM_OPTIONS, termTypeLabel)
 *        ─→ BillPaymentSection select + due-date hint.
 *
 * DEPENDENCIES: ./types (SupplierTermType only — a type import, no runtime dep).
 */
import type { BillTermSource, BillTermType, SupplierTermType } from './types';

/**
 * One selectable supplier payment term. ``shortLabel`` is the dense badge
 * wording ("Net 30"); ``label`` is the full dropdown wording ("30 days").
 * Order mirrors how the operator dictated them: CoD → EOD → 7/30/45/60/90 →
 * EOM → Custom.
 */
export interface SupplierTermOption {
  value: SupplierTermType;
  label: string;
  shortLabel: string;
}

export const SUPPLIER_TERM_OPTIONS: readonly SupplierTermOption[] = [
  { value: 'cod', label: 'Cash on delivery (CoD)', shortLabel: 'CoD' },
  { value: 'eod', label: 'End of day', shortLabel: 'EOD' },
  { value: 'net_7', label: '7 days', shortLabel: 'Net 7' },
  { value: 'net_30', label: '30 days', shortLabel: 'Net 30' },
  { value: 'net_45', label: '45 days', shortLabel: 'Net 45' },
  { value: 'net_60', label: '60 days', shortLabel: 'Net 60' },
  { value: 'net_90', label: '90 days', shortLabel: 'Net 90' },
  { value: 'eom', label: 'End of month', shortLabel: 'EOM' },
  { value: 'custom', label: 'Custom…', shortLabel: 'Custom' },
] as const;

/**
 * The bill-scope-only exemption chip. Rendered apart from
 * ``SUPPLIER_TERM_OPTIONS`` because it is not a term the vendor can
 * carry — it is this bill opting out of whatever the vendor's term is.
 */
export const EXEMPT_TERM_OPTION = {
  value: 'exempt',
  label: 'Exempt — this bill follows no term',
  shortLabel: 'Exempt',
} as const;

const TERM_LABELS: Record<SupplierTermType, string> = {
  cod: 'Cash on delivery',
  eod: 'End of day',
  net_7: '7 days',
  net_30: '30 days',
  net_45: '45 days',
  net_60: '60 days',
  net_90: '90 days',
  eom: 'End of month',
  custom: 'Custom',
};

/**
 * Human label for a supplier term. Prefer the server's
 * ``supplier_detail.payment_term_label`` when present; this is the local
 * fallback (and the only source for Custom's day count).
 *
 * Inputs:  ``termType`` — a SupplierTermType or null/'' when unset.
 *          ``customDays`` — day offset, only meaningful when type==='custom'.
 * Output:  Display string; '' when there is no term set.
 */
export function termTypeLabel(
  termType: BillTermType | '' | null | undefined,
  customDays?: number | null,
): string {
  if (!termType) return '';
  if (termType === 'exempt') return 'Exempt (no term)';
  if (termType === 'custom') {
    const n = customDays ?? null;
    return n != null && Number.isFinite(n) ? `Custom (${n} days)` : 'Custom';
  }
  return TERM_LABELS[termType] ?? '';
}

/**
 * Plain-English note about where a bill's term came from, for the line
 * under the chips. Empty when there is no term to explain.
 */
export function termSourceNote(source: BillTermSource): string {
  switch (source) {
    case 'bill':
      return 'this bill only';
    case 'supplier':
      return 'from supplier';
    case 'exempt':
      return 'this bill is exempt';
    default:
      return '';
  }
}

/** Type guard: is the string one of the known supplier term codes? */
export function isSupplierTermType(v: unknown): v is SupplierTermType {
  return typeof v === 'string' && v in TERM_LABELS;
}
